// Migration Tool - Migrate existing competitions to League structure
// This runs in-browser with authenticated user
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card } from '../components';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { v4 as uuidv4 } from 'uuid';

const MigrationTool = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('idle'); // idle, running, complete, error
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);

  const addLog = (message) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()} - ${message}`]);
  };

  const runMigration = async () => {
    if (!user) {
      addLog('ERROR: You must be logged in to run the migration');
      return;
    }

    setStatus('running');
    setLogs([]);
    addLog('Starting migration to Leagues structure...');

    try {
      // 1. Get all competitions
      addLog('Step 1: Loading all competitions...');
      const competitionsRef = collection(db, 'competitions');
      const competitionsSnap = await getDocs(competitionsRef);
      const competitions = competitionsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      addLog(`Found ${competitions.length} competitions`);

      // 2. Get all players
      addLog('Step 2: Loading all players...');
      const playersRef = collection(db, 'players');
      const playersSnap = await getDocs(playersRef);
      const players = playersSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      addLog(`Found ${players.length} players`);

      // 3. Get all games
      addLog('Step 3: Loading all games...');
      const gamesRef = collection(db, 'games');
      const gamesSnap = await getDocs(gamesRef);
      const games = gamesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      addLog(`Found ${games.length} games`);

      // 4. Get all categories
      addLog('Step 4: Loading all categories...');
      const categoriesRef = collection(db, 'categories');
      const categoriesSnap = await getDocs(categoriesRef);
      const categories = categoriesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      addLog(`Found ${categories.length} categories`);

      // 5. Group competitions by organizer (only those not already linked)
      addLog('Step 5: Grouping competitions by organizer...');
      const competitionsByOrganizer = {};
      competitions.forEach(comp => {
        if (!comp.leagueId) {
          const organizerId = comp.organizerId;
          if (!competitionsByOrganizer[organizerId]) {
            competitionsByOrganizer[organizerId] = [];
          }
          competitionsByOrganizer[organizerId].push(comp);
        }
      });

      const organizerCount = Object.keys(competitionsByOrganizer).length;
      addLog(`Found ${organizerCount} unique organizers with unmigrated competitions`);

      if (organizerCount === 0) {
        addLog('No competitions to migrate - all are already linked to leagues');
        setStatus('complete');
        setSummary({ leaguesCreated: 0, competitionsMigrated: 0, gameSectionsCreated: 0 });
        return;
      }

      // 6. Create leagues and migrate data
      addLog('Step 6: Creating leagues and migrating data...');
      let leaguesCreated = 0;
      let competitionsMigrated = 0;
      let gameSectionsCreated = 0;

      for (const [organizerId, orgCompetitions] of Object.entries(competitionsByOrganizer)) {
        addLog(`Processing organizer: ${organizerId} (${orgCompetitions.length} competitions)`);

        // Get organizer name
        let organizerName = 'Unknown';
        const firstComp = orgCompetitions[0];
        const compPlayers = players.filter(p => p.competitionId === firstComp.id);
        const organizer = compPlayers.find(p => p.odplayerId === organizerId);
        if (organizer) {
          organizerName = organizer.displayName || 'Unknown';
        }

        // Create league
        const leagueId = uuidv4();
        const leagueName = `${organizerName}'s League`;

        // Collect all unique players
        const allCompetitionIds = orgCompetitions.map(c => c.id);
        const leaguePlayers = players.filter(p => allCompetitionIds.includes(p.competitionId));

        const memberMap = new Map();
        leaguePlayers.forEach(p => {
          if (!memberMap.has(p.odplayerId)) {
            memberMap.set(p.odplayerId, {
              playerId: p.odplayerId,
              displayName: p.displayName || 'Unknown',
              role: p.odplayerId === organizerId ? 'admin' : 'member',
              joinedAt: new Date().toISOString()
            });
          }
        });
        const members = Array.from(memberMap.values());
        const memberIds = members.map(m => m.playerId);

        addLog(`Creating league: ${leagueName} with ${members.length} members`);

        // Create league document
        const leagueDoc = {
          name: leagueName,
          description: `Migrated league for ${organizerName}`,
          adminId: organizerId,
          adminName: organizerName,
          members,
          memberIds,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        await setDoc(doc(db, 'leagues', leagueId), leagueDoc);
        leaguesCreated++;
        addLog(`League created: ${leagueId}`);

        // 7. Create Game Sections
        addLog('Creating Game Sections...');
        const compGames = games.filter(g => allCompetitionIds.includes(g.competitionId));
        const gameMap = new Map();

        compGames.forEach(game => {
          const category = categories.find(c =>
            c.id === game.categoryId ||
            (c.competitionId === game.competitionId && c.name === game.category)
          );
          const categoryName = category?.name || game.category || 'Uncategorized';
          const categoryId = category?.id || categoryName.toLowerCase().replace(/\s+/g, '-');

          const gameKey = `${game.name}|${categoryName}`;

          if (!gameMap.has(gameKey)) {
            gameMap.set(gameKey, {
              name: game.name,
              categoryId,
              categoryName,
              totalTimesPlayed: 0,
              totalDurationMinutes: 0
            });
          }

          const gameData = gameMap.get(gameKey);
          gameData.totalTimesPlayed += 1;

          if (game.actualStartTime && game.actualEndTime) {
            const start = new Date(game.actualStartTime);
            const end = new Date(game.actualEndTime);
            const duration = Math.round((end - start) / 60000);
            if (duration > 0 && duration < 600) {
              gameData.totalDurationMinutes += duration;
            }
          }
        });

        for (const [, gameData] of gameMap) {
          const gameSectionId = uuidv4();
          const averageDuration = gameData.totalTimesPlayed > 0 && gameData.totalDurationMinutes > 0
            ? Math.round(gameData.totalDurationMinutes / gameData.totalTimesPlayed)
            : null;

          const gameSectionDoc = {
            leagueId,
            name: gameData.name,
            categoryId: gameData.categoryId,
            categoryName: gameData.categoryName,
            photoUrl: null,
            rulesUrl: '',
            averageDurationMinutes: averageDuration,
            totalTimesPlayed: gameData.totalTimesPlayed,
            totalDurationMinutes: gameData.totalDurationMinutes,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          };

          await setDoc(doc(db, 'gameSections', gameSectionId), gameSectionDoc);
          gameSectionsCreated++;
          addLog(`Game Section created: ${gameData.name} (${gameData.categoryName})`);
        }

        // 8. Link competitions to league
        addLog('Linking competitions to league...');
        for (const comp of orgCompetitions) {
          await updateDoc(doc(db, 'competitions', comp.id), {
            leagueId,
            updatedAt: serverTimestamp()
          });
          competitionsMigrated++;
          addLog(`Linked: ${comp.name}`);
        }
      }

      addLog('===========================================');
      addLog('Migration completed successfully!');
      addLog('===========================================');

      setSummary({
        leaguesCreated,
        competitionsMigrated,
        gameSectionsCreated
      });
      setStatus('complete');

    } catch (error) {
      addLog(`ERROR: ${error.message}`);
      console.error('Migration failed:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            ← Back to Home
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 mt-2">Migration Tool</h1>
          <p className="text-gray-600">Migrate existing competitions to League structure</p>
        </div>

        {!user && (
          <Card className="mb-6 bg-yellow-50 border-yellow-200">
            <p className="text-yellow-800">You must be logged in to run the migration.</p>
          </Card>
        )}

        <Card className="mb-6">
          <h2 className="text-lg font-bold mb-4">What this migration does:</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>Creates a League for each unique competition organizer</li>
            <li>Links all their competitions to the new league</li>
            <li>Extracts unique games and creates Game Sections with calculated average durations</li>
            <li>Adds all players from competitions as league members</li>
          </ul>
          <p className="mt-4 text-sm text-gray-500">
            Note: Competitions already linked to a league will be skipped.
          </p>
        </Card>

        {status === 'idle' && (
          <Button onClick={runMigration} disabled={!user}>
            Start Migration
          </Button>
        )}

        {status === 'running' && (
          <Card className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="font-medium">Migration in progress...</span>
            </div>
          </Card>
        )}

        {summary && (
          <Card className="mb-6 bg-green-50 border-green-200">
            <h3 className="font-bold text-green-900 mb-2">Migration Summary</h3>
            <ul className="space-y-1 text-green-800">
              <li>Leagues created: {summary.leaguesCreated}</li>
              <li>Competitions migrated: {summary.competitionsMigrated}</li>
              <li>Game Sections created: {summary.gameSectionsCreated}</li>
            </ul>
          </Card>
        )}

        {logs.length > 0 && (
          <Card>
            <h3 className="font-bold mb-2">Migration Log</h3>
            <div className="bg-gray-900 text-green-400 p-4 rounded font-mono text-sm max-h-96 overflow-y-auto">
              {logs.map((log, i) => (
                <div key={i} className={log.includes('ERROR') ? 'text-red-400' : ''}>
                  {log}
                </div>
              ))}
            </div>
          </Card>
        )}

        {status === 'complete' && (
          <div className="mt-6">
            <Button onClick={() => navigate('/leagues')}>
              View Your Leagues
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MigrationTool;
